import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { createTypedSupabaseClient, type TypedSupabaseClient } from '@t90/common/supabase';

// =====================================================
// QUERY CLIENT CONFIGURATION
// =====================================================

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Data is considered fresh for 30 seconds
        staleTime: 30 * 1000,
        // Cache data for 5 minutes
        gcTime: 5 * 60 * 1000,
        // Retry failed requests 3 times with exponential backoff
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Refetch on window focus for real-time feel
        refetchOnWindowFocus: true,
        // Don't refetch on mount if data is fresh
        refetchOnMount: true,
      },
      mutations: {
        // Retry mutations once on failure
        retry: 1,
      },
    },
  });
}

// =====================================================
// SUPABASE CONTEXT
// =====================================================

interface SupabaseContextValue {
  supabase: TypedSupabaseClient;
}

const SupabaseContext = createContext<SupabaseContextValue | null>(null);

export function useSupabase(): TypedSupabaseClient {
  const context = useContext(SupabaseContext);
  if (!context) {
    throw new Error('useSupabase must be used within an ApiProvider');
  }
  return context.supabase;
}

// =====================================================
// API PROVIDER
// =====================================================

interface ApiProviderProps {
  children: ReactNode;
  supabaseUrl: string;
  supabaseAnonKey: string;
  queryClient?: QueryClient;
}

export function ApiProvider({
  children,
  supabaseUrl,
  supabaseAnonKey,
  queryClient: externalQueryClient,
}: ApiProviderProps) {
  const queryClient = useMemo(
    () => externalQueryClient ?? createQueryClient(),
    [externalQueryClient]
  );

  const supabase = useMemo(
    () => createTypedSupabaseClient(supabaseUrl, supabaseAnonKey),
    [supabaseUrl, supabaseAnonKey]
  );

  const contextValue = useMemo(() => ({ supabase }), [supabase]);

  return (
    <SupabaseContext.Provider value={contextValue}>
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    </SupabaseContext.Provider>
  );
}

// =====================================================
// QUERY KEY FACTORY
// =====================================================

export const queryKeys = {
  // Locations
  locations: {
    all: ['locations'] as const,
    list: (filters?: { active?: boolean }) => [...queryKeys.locations.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.locations.all, 'detail', id] as const,
  },
  // Customers
  customers: {
    all: ['customers'] as const,
    list: (filters?: { locationId?: string; active?: boolean }) =>
      [...queryKeys.customers.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.customers.all, 'detail', id] as const,
  },
  // Orders
  orders: {
    all: ['orders'] as const,
    list: (filters?: { customerId?: string; status?: string; locationId?: string }) =>
      [...queryKeys.orders.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.orders.all, 'detail', id] as const,
  },
  // Projects
  projects: {
    all: ['projects'] as const,
    list: (filters?: { orderId?: string; status?: string; locationId?: string }) =>
      [...queryKeys.projects.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.projects.all, 'detail', id] as const,
  },
  // Trainees
  trainees: {
    all: ['trainees'] as const,
    list: (filters?: { locationId?: string; status?: string; active?: boolean }) =>
      [...queryKeys.trainees.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.trainees.all, 'detail', id] as const,
    certifications: (traineeId: string) =>
      [...queryKeys.trainees.all, 'certifications', traineeId] as const,
  },
  // Team Leads
  teamLeads: {
    all: ['teamLeads'] as const,
    list: (filters?: { locationId?: string; active?: boolean }) =>
      [...queryKeys.teamLeads.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.teamLeads.all, 'detail', id] as const,
  },
  // Production
  production: {
    all: ['production'] as const,
    assignments: (filters?: { projectId?: string; traineeId?: string; date?: string }) =>
      [...queryKeys.production.all, 'assignments', filters] as const,
    logs: (assignmentId: string) =>
      [...queryKeys.production.all, 'logs', assignmentId] as const,
  },
  // Quality
  quality: {
    all: ['quality'] as const,
    checks: (filters?: { projectId?: string }) =>
      [...queryKeys.quality.all, 'checks', filters] as const,
    detail: (id: string) => [...queryKeys.quality.all, 'detail', id] as const,
  },
  // Inventory
  inventory: {
    all: ['inventory'] as const,
    materials: (filters?: { categoryId?: string }) =>
      [...queryKeys.inventory.all, 'materials', filters] as const,
    stockLevels: (locationId?: string) =>
      [...queryKeys.inventory.all, 'stockLevels', locationId] as const,
  },
  // Service Types & Items
  services: {
    all: ['services'] as const,
    types: () => [...queryKeys.services.all, 'types'] as const,
    items: (serviceTypeId?: string) => [...queryKeys.services.all, 'items', serviceTypeId] as const,
  },
  // App Users
  users: {
    all: ['users'] as const,
    list: (filters?: { role?: string; locationId?: string }) =>
      [...queryKeys.users.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.users.all, 'detail', id] as const,
    current: () => [...queryKeys.users.all, 'current'] as const,
  },
} as const;
